import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Play, 
  Square, 
  RefreshCw, 
  Eye, 
  Zap, 
  Settings,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Loader,
  Database,
  Layers,
  Radio,
  Terminal,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { MainLayout } from '../layout/MainLayout';
import apiClient from '../utils/apiClient';

const RayTracingExecution = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [projectName, setProjectName] = useState(null);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  
  const [preRayTracingStatus, setPreRayTracingStatus] = useState({
    isRunning: false,
    status: 'idle', 
    logs: [],
    podLogs: [],
    combinedLogs: [],
    lastLog: null,
    podName: null,
    lastUpdated: null,
    sensorsCount: null,
    clustersCount: null,
    surfacesCount: null,
    unprocessedSensorsCount: null
  });
  
  const [rayTracingStatus, setRayTracingStatus] = useState({
    isRunning: false,
    status: 'idle', 
    logs: [],
    podLogs: [],
    combinedLogs: [],
    lastLog: null,
    podName: null,
    lastUpdated: null,
    sensorsCount: null,
    clustersCount: null,
    surfacesCount: null,
    unprocessedSensorsCount: null
  });
  
  const [loadingStates, setLoadingStates] = useState({
    preRayTracingStart: false,
    preRayTracingStop: false,
    preRayTracingStatus: false,
    rayTracingStart: false,
    rayTracingStop: false,
    rayTracingStatus: false,
    deleteSensors: false,
    deleteSurfaces: false,
    deleteClusters: false,
    deleteClusterClusters: false
  });

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    entityType: ''
  });

  useEffect(() => {
    const fetchProjectName = async () => {
      try {
        setIsLoadingProject(true);
        const data = await apiClient.get(`/api/projects/${projectId}`, {
          useOperational: true,
          showToast: false,
        });
        setProjectName(data.name);
      } catch (error) {
        console.error('Error fetching project name:', error);
        setProjectName(`Project_${projectId}`);
      } finally {
        setIsLoadingProject(false);
      }
    };

    if (projectId) {
      fetchProjectName();
    }
  }, [projectId]);

  // Add useEffect to load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      if (projectId && projectName) {
        // Load initial counts for all data types
        await Promise.all([
          checkPreRayTracingStatus(),
          checkRayTracingStatus()
        ]);
      }
    };

    loadInitialData();
  }, [projectId, projectName]); // Depend on projectName so it loads after project name is fetched

  // Add polling during deletion operations
  useEffect(() => {
    let intervalId;
    
    // Poll every 2 seconds while any deletion is in progress
    if (loadingStates.deleteSensors || loadingStates.deleteSurfaces || loadingStates.deleteClusters) {
      intervalId = setInterval(async () => {
        if (projectId && projectName) {
          try {
            await Promise.all([
              checkPreRayTracingStatus(),
              checkRayTracingStatus()
            ]);
          } catch (error) {
            console.error('Error during polling:', error);
          }
        }
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loadingStates.deleteSensors, loadingStates.deleteSurfaces, loadingStates.deleteClusters, projectId, projectName]);

  const setLoading = useCallback((key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  }, []);

  // Helper function to process and combine logs
  const processLogs = (logs, podLogs) => {
    const processedLogs = logs ? logs.split('\n').filter(log => log.trim()) : [];
    const processedPodLogs = podLogs ? podLogs.split('\n').filter(log => log.trim()) : [];
    
    // Combine all logs
    const combinedLogs = [
      ...processedLogs.map(log => ({ text: log, type: 'execution' })),
      ...processedPodLogs.map(log => ({ text: log, type: 'pod' }))
    ];
    
    // Get the last log entry
    const lastLog = combinedLogs.length > 0 ? combinedLogs[combinedLogs.length - 1] : null;
    
    return {
      logs: processedLogs,
      podLogs: processedPodLogs,
      combinedLogs,
      lastLog
    };
  };

  // Delete functions
  const deleteEntityData = async (entityType, endpoint, loadingKey) => {
    // Close dialog immediately and show loading state
    setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: null, entityType: '' });
    setLoading(loadingKey, true);
    
    try {
      await apiClient.delete(`/api/${endpoint}/${projectId}`, {
        useOperational: true
      });
      
      // Refresh status to update counts
      await Promise.all([
        checkPreRayTracingStatus(),
        checkRayTracingStatus()
      ]);
      
      console.log(`${entityType} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting ${entityType}:`, error);
    } finally {
      setLoading(loadingKey, false);
    }
  };

  const openConfirmationDialog = (entityType, endpoint, loadingKey) => {
    setConfirmationDialog({
      isOpen: true,
      title: `Delete ${entityType}`,
      message: `Are you sure you want to delete all ${entityType.toLowerCase()} for this project? This action cannot be undone.`,
      onConfirm: () => deleteEntityData(entityType, endpoint, loadingKey),
      entityType
    });
  };

  // Delete handlers
  const deleteSensors = () => openConfirmationDialog('Sensors', 'sensors/delete-by-projectId', 'deleteSensors');
  const deleteSurfaces = () => openConfirmationDialog('Surfaces', 'surface/delete-by-projectId', 'deleteSurfaces');
  const deleteClusters = () => openConfirmationDialog('Clusters', 'clusters/delete-by-projectId', 'deleteClusters');

  const runPreRayTracing = async () => {
    if (!projectName) return;
    
    setLoading('preRayTracingStart', true);
    try {
      const response = await apiClient.post('/submit-job', {
        payload: projectName,
        priority: 7,
        projectId: parseInt(projectId),
        subType: "preraytracing",
        type: "raytracing"
      }, {
        useOperational: 'execution'
      });
      
      setPreRayTracingStatus(prev => ({
        ...prev,
        isRunning: true,
        status: 'running',
        lastUpdated: new Date().toLocaleTimeString()
      }));
      
      console.log('Pre-raytracing started:', response);
    } catch (error) {
      console.error('Error starting pre-raytracing:', error);
      setPreRayTracingStatus(prev => ({
        ...prev,
        status: 'error',
        lastUpdated: new Date().toLocaleTimeString()
      }));
    } finally {
      setLoading('preRayTracingStart', false);
    }
  };

  const runRayTracing = async () => {
    if (!projectName) return;
    
    setLoading('rayTracingStart', true);
    try {
      const response = await apiClient.post('/submit-job', {
        payload: projectName,
        priority: 7,
        projectId: parseInt(projectId),
        subType: "raytracing",
        type: "raytracing"
      }, {
        useOperational: 'execution'
      });
      
      setRayTracingStatus(prev => ({
        ...prev,
        isRunning: true,
        status: 'running',
        lastUpdated: new Date().toLocaleTimeString()
      }));
      
      console.log('Raytracing started:', response);
    } catch (error) {
      console.error('Error starting raytracing:', error);
      setRayTracingStatus(prev => ({
        ...prev,
        status: 'error',
        lastUpdated: new Date().toLocaleTimeString()
      }));
    } finally {
      setLoading('rayTracingStart', false);
    }
  };

  const stopPreRayTracing = async () => {
    if (!projectName) return;
    
    setLoading('preRayTracingStop', true);
    try {
      const response = await apiClient.post('/kill-pod', {
        podPrefix: projectName,
        projectId: parseInt(projectId),
        subType: "preraytracing"
      }, {
        useOperational: 'execution'
      });
      
      setPreRayTracingStatus(prev => ({
        ...prev,
        isRunning: false,
        status: 'idle',
        lastUpdated: new Date().toLocaleTimeString()
      }));
      
      console.log('Pre-raytracing stopped:', response);
    } catch (error) {
      console.error('Error stopping pre-raytracing:', error);
    } finally {
      setLoading('preRayTracingStop', false);
    }
  };

  const stopRayTracing = async () => {
    if (!projectName) return;
    
    setLoading('rayTracingStop', true);
    try {
      const response = await apiClient.post('/kill-pod', {
        podPrefix: projectName,
        projectId: parseInt(projectId),
        subType: "raytracing"
      }, {
        useOperational: 'execution'
      });
      
      setRayTracingStatus(prev => ({
        ...prev,
        isRunning: false,
        status: 'idle',
        lastUpdated: new Date().toLocaleTimeString()
      }));
      
      console.log('Raytracing stopped:', response);
    } catch (error) {
      console.error('Error stopping raytracing:', error);
    } finally {
      setLoading('rayTracingStop', false);
    }
  };

  // const checkPreRayTracingStatus = async () => {
  //   if (!projectName) return;
    
  //   setLoading('preRayTracingStatus', true);
  //   try {
  //     const [sensorsResponse, clustersResponse, surfacesResponse] = await Promise.all([
  //       apiClient.get(`/api/sensors/get_not_processed_sensors?projectId=${projectId}`, {
  //         useOperational: true
  //       }),
  //       apiClient.get(`/api/clusters?projectId=${projectId}`, {
  //         useOperational: true
  //       }),
  //       apiClient.get(`/api/surfaces?projectId=${projectId}`, {
  //         useOperational: true
  //       })
  //     ]);

  //     // Handle the Go API response structure properly
  //     let unprocessedSensorsCount = 0;
  //     if (sensorsResponse.sensors && Array.isArray(sensorsResponse.sensors)) {
  //       unprocessedSensorsCount = sensorsResponse.sensors.length;
  //     } else if (sensorsResponse.message && sensorsResponse.message.includes("No not-processed sensors found")) {
  //       unprocessedSensorsCount = 0;
  //     }

  //     const dataUpdates = {
  //       clustersCount: clustersResponse.maxItems || 0,
  //       surfacesCount: surfacesResponse.maxItems || 0,
  //       unprocessedSensorsCount: unprocessedSensorsCount,
  //       lastUpdated: new Date().toLocaleTimeString()
  //     };

  //     let status = 'idle';
  //     let isRunning = false;
  //     let logData = { logs: [], podLogs: [], combinedLogs: [], lastLog: null };
  //     let podName = null;

  //     try {
  //       const podStatusResponse = await apiClient.post('/pod-status', {
  //         projectName: projectName,
  //         projectId: parseInt(projectId),
  //         subType: "preraytracing"
  //       }, {
  //         useOperational: 'execution'
  //       });

  //       if (podStatusResponse.status === 'Running') {
  //         status = 'running';
  //         isRunning = true;
  //       } else if (podStatusResponse.status === 'Completed') {
  //         status = 'completed';
  //       } else if (podStatusResponse.status === 'Failed') {
  //         status = 'error';
  //       }

  //       logData = processLogs(podStatusResponse.logs, podStatusResponse.podLog);
  //       podName = podStatusResponse.podName;

  //     } catch (podError) {
  //       console.warn('Pod status check failed:', podError);
  //       status = 'idle';
        
  //       const errorMessage = podError.message || 'No pod found for project';
  //       let logMessage = `Error checking pod status: ${errorMessage}`;
  //       let logType = 'error';
        
  //       // Handle "No pod found for project" case differently
  //       if (errorMessage.includes('No pod found for project')) {
  //         logType = 'info';
  //         const unprocessedCount = dataUpdates.unprocessedSensorsCount;
          
  //         if (unprocessedCount === 0) {
  //           logMessage = `Server log disappeared. Execution is finished - no unprocessed sensors remaining.`;
  //           status = 'completed';
  //         } else {
  //           logMessage = `Server log disappeared. Something may have happened - ${unprocessedCount} sensors still unprocessed.`;
  //           status = 'error';
  //         }
  //       }
        
  //       logData = {
  //         logs: [logMessage],
  //         podLogs: [],
  //         combinedLogs: [{ text: logMessage, type: logType }],
  //         lastLog: { text: logMessage, type: logType }
  //       };
  //     }

  //     setPreRayTracingStatus(prev => ({
  //       ...prev,
  //       status,
  //       isRunning,
  //       ...logData,
  //       podName,
  //       ...dataUpdates
  //     }));
      
  //   } catch (error) {
  //     console.error('Error checking pre-raytracing status:', error);
      
  //     const errorMessage = error.message || 'Unknown error';
  //     let logMessage = `Error checking status: ${errorMessage}`;
  //     let logType = 'error';
  //     let finalStatus = 'error';
      
  //     // Handle "No pod found for project" case differently
  //     if (errorMessage.includes('No pod found for project')) {
  //       logType = 'info';
  //       // We don't have dataUpdates here, so we can't check unprocessed count
  //       logMessage = `Server log disappeared. Check unprocessed sensors to determine if execution finished.`;
  //       finalStatus = 'idle';
  //     }
      
  //     setPreRayTracingStatus(prev => ({
  //       ...prev,
  //       status: finalStatus,
  //       logs: [logMessage],
  //       combinedLogs: [{ text: logMessage, type: logType }],
  //       lastLog: { text: logMessage, type: logType },
  //       lastUpdated: new Date().toLocaleTimeString()
  //     }));
  //   } finally {
  //     setLoading('preRayTracingStatus', false);
  //   }
  // };
const checkPreRayTracingStatus = async () => {
  if (!projectName) return;
  
  setLoading('preRayTracingStatus', true);
  try {
    const [sensorsResponse, allSensorsResponse, clustersResponse, surfacesResponse] = await Promise.all([
      apiClient.get(`/api/sensors/get_not_processed_sensors?projectId=${projectId}`, {
        useOperational: true
      }),
      apiClient.get(`/api/sensors?projectId=${projectId}&fields[]=id&fields[]=cdif&fields[]=area`, {
        useOperational: true
      }),
      apiClient.get(`/api/clusters?projectId=${projectId}`, {
        useOperational: true
      }),
      apiClient.get(`/api/surfaces?projectId=${projectId}`, {
        useOperational: true
      })
    ]);

    // Handle the Go API response structure properly for unprocessed sensors
    let unprocessedSensorsCount = 0;
    if (sensorsResponse.sensors && Array.isArray(sensorsResponse.sensors)) {
      unprocessedSensorsCount = sensorsResponse.sensors.length;
    } else if (sensorsResponse.message && sensorsResponse.message.includes("No not-processed sensors found")) {
      unprocessedSensorsCount = 0;
    }

    // Handle the all sensors response to get total count
    let totalSensorsCount = 0;
    if (allSensorsResponse && allSensorsResponse.maxItems !== undefined) {
      totalSensorsCount = allSensorsResponse.maxItems;
    }

    const dataUpdates = {
      sensorsCount: totalSensorsCount, // Total sensors count
      clustersCount: clustersResponse.maxItems || 0,
      surfacesCount: surfacesResponse.maxItems || 0,
      unprocessedSensorsCount: unprocessedSensorsCount, // Unprocessed sensors count
      lastUpdated: new Date().toLocaleTimeString()
    };

    // ... rest of the function remains the same
    let status = 'idle';
    let isRunning = false;
    let logData = { logs: [], podLogs: [], combinedLogs: [], lastLog: null };
    let podName = null;

    try {
      const podStatusResponse = await apiClient.post('/pod-status', {
        projectName: projectName,
        projectId: parseInt(projectId),
        subType: "preraytracing"
      }, {
        useOperational: 'execution'
      });

      if (podStatusResponse.status === 'Running') {
        status = 'running';
        isRunning = true;
      } else if (podStatusResponse.status === 'Completed') {
        status = 'completed';
      } else if (podStatusResponse.status === 'Failed') {
        status = 'error';
      }

      logData = processLogs(podStatusResponse.logs, podStatusResponse.podLog);
      podName = podStatusResponse.podName;

    } catch (podError) {
      console.warn('Pod status check failed:', podError);
      status = 'idle';
      
      const errorMessage = podError.message || 'No pod found for project';
      let logMessage = `Error checking pod status: ${errorMessage}`;
      let logType = 'error';
      
      if (errorMessage.includes('No pod found for project')) {
        logType = 'info';
        const unprocessedCount = dataUpdates.unprocessedSensorsCount;
        
        if (unprocessedCount === 0) {
          logMessage = `Server log disappeared. Execution is finished - no unprocessed sensors remaining.`;
          status = 'completed';
        } else {
          logMessage = `Server log disappeared. Something may have happened - ${unprocessedCount} sensors still unprocessed.`;
          status = 'error';
        }
      }
      
      logData = {
        logs: [logMessage],
        podLogs: [],
        combinedLogs: [{ text: logMessage, type: logType }],
        lastLog: { text: logMessage, type: logType }
      };
    }

    setPreRayTracingStatus(prev => ({
      ...prev,
      status,
      isRunning,
      ...logData,
      podName,
      ...dataUpdates
    }));
    
  } catch (error) {
    console.error('Error checking pre-raytracing status:', error);
    
    const errorMessage = error.message || 'Unknown error';
    let logMessage = `Error checking status: ${errorMessage}`;
    let logType = 'error';
    let finalStatus = 'error';
    
    if (errorMessage.includes('No pod found for project')) {
      logType = 'info';
      logMessage = `Server log disappeared. Check unprocessed sensors to determine if execution finished.`;
      finalStatus = 'idle';
    }
    
    setPreRayTracingStatus(prev => ({
      ...prev,
      status: finalStatus,
      logs: [logMessage],
      combinedLogs: [{ text: logMessage, type: logType }],
      lastLog: { text: logMessage, type: logType },
      lastUpdated: new Date().toLocaleTimeString()
    }));
  } finally {
    setLoading('preRayTracingStatus', false);
  }
};
const checkRayTracingStatus = async () => {
  if (!projectName) return;
  
  setLoading('rayTracingStatus', true);
  try {
    const [sensorsResponse, allSensorsResponse, clustersResponse, surfacesResponse] = await Promise.all([
      apiClient.get(`/api/sensors/get_not_processed_sensors?projectId=${projectId}`, {
        useOperational: true
      }),
      apiClient.get(`/api/sensors?projectId=${projectId}&fields[]=id&fields[]=cdif&fields[]=area`, {
        useOperational: true
      }),
      apiClient.get(`/api/clusters?projectId=${projectId}`, {
        useOperational: true
      }),
      apiClient.get(`/api/surfaces?projectId=${projectId}`, {
        useOperational: true
      })
    ]);

    // Handle the Go API response structure properly for unprocessed sensors
    let unprocessedSensorsCount = 0;
    if (sensorsResponse.sensors && Array.isArray(sensorsResponse.sensors)) {
      unprocessedSensorsCount = sensorsResponse.sensors.length;
    } else if (sensorsResponse.message && sensorsResponse.message.includes("No not-processed sensors found")) {
      unprocessedSensorsCount = 0;
    }

    // Handle the all sensors response to get total count
    let totalSensorsCount = 0;
    if (allSensorsResponse && allSensorsResponse.maxItems !== undefined) {
      totalSensorsCount = allSensorsResponse.maxItems;
    }

    const dataUpdates = {
      sensorsCount: totalSensorsCount, // Total sensors count
      clustersCount: clustersResponse.maxItems || 0,
      surfacesCount: surfacesResponse.maxItems || 0,
      unprocessedSensorsCount: unprocessedSensorsCount, // Unprocessed sensors count
      lastUpdated: new Date().toLocaleTimeString()
    };

    // ... rest of the function remains the same
    let status = 'idle';
    let isRunning = false;
    let logData = { logs: [], podLogs: [], combinedLogs: [], lastLog: null };
    let podName = null;

    try {
      const podStatusResponse = await apiClient.post('/pod-status', {
        projectName: projectName,
        projectId: parseInt(projectId),
        subType: "raytracing"
      }, {
        useOperational: 'execution'
      });

      if (podStatusResponse.status === 'Running') {
        status = 'running';
        isRunning = true;
      } else if (podStatusResponse.status === 'Completed') {
        if (unprocessedSensorsCount === 0) {
          status = 'completed';
        } else {
          status = 'running'; 
          isRunning = true;
        }
      } else if (podStatusResponse.status === 'Failed') {
        status = 'error';
      }

      logData = processLogs(podStatusResponse.logs, podStatusResponse.podLog);
      podName = podStatusResponse.podName;

    } catch (podError) {
      console.warn('Pod status check failed:', podError);
      status = 'idle';
      
      const errorMessage = podError.message || 'No pod found for project';
      let logMessage = `Error checking pod status: ${errorMessage}`;
      let logType = 'error';
      
      if (errorMessage.includes('No pod found for project')) {
        logType = 'info';
        const unprocessedCount = dataUpdates.unprocessedSensorsCount;
        
        if (unprocessedCount === 0) {
          logMessage = `Server log disappeared. Execution is finished - no unprocessed sensors remaining.`;
          status = 'completed';
        } else {
          logMessage = `Server log disappeared. Something may have happened - ${unprocessedCount} sensors still unprocessed.`;
          status = 'error';
        }
      }
      
      logData = {
        logs: [logMessage],
        podLogs: [],
        combinedLogs: [{ text: logMessage, type: logType }],
        lastLog: { text: logMessage, type: logType }
      };
    }

    setRayTracingStatus(prev => ({
      ...prev,
      status,
      isRunning,
      ...logData,
      podName,
      ...dataUpdates
    }));
    
  } catch (error) {
    console.error('Error checking raytracing status:', error);
    
    const errorMessage = error.message || 'Unknown error';
    let logMessage = `Error checking status: ${errorMessage}`;
    let logType = 'error';
    let finalStatus = 'error';
    
    if (errorMessage.includes('No pod found for project')) {
      logType = 'info';
      logMessage = `Server log disappeared. Check unprocessed sensors to determine if execution finished.`;
      finalStatus = 'idle';
    }
    
    setRayTracingStatus(prev => ({
      ...prev,
      status: finalStatus,
      logs: [logMessage],
      combinedLogs: [{ text: logMessage, type: logType }],
      lastLog: { text: logMessage, type: logType },
      lastUpdated: new Date().toLocaleTimeString()
    }));
  } finally {
    setLoading('rayTracingStatus', false);
  }
};
  // const checkRayTracingStatus = async () => {
  //   if (!projectName) return;
    
  //   setLoading('rayTracingStatus', true);
  //   try {
  //     const [sensorsResponse, clustersResponse, surfacesResponse] = await Promise.all([
  //       apiClient.get(`/api/sensors/get_not_processed_sensors?projectId=${projectId}`, {
  //         useOperational: true
  //       }),
  //       apiClient.get(`/api/clusters?projectId=${projectId}`, {
  //         useOperational: true
  //       }),
  //       apiClient.get(`/api/surfaces?projectId=${projectId}`, {
  //         useOperational: true
  //       })
  //     ]);

  //     // Handle the Go API response structure properly
  //     let unprocessedSensorsCount = 0;
  //     if (sensorsResponse.sensors && Array.isArray(sensorsResponse.sensors)) {
  //       unprocessedSensorsCount = sensorsResponse.sensors.length;
  //     } else if (sensorsResponse.message && sensorsResponse.message.includes("No not-processed sensors found")) {
  //       unprocessedSensorsCount = 0;
  //     }

  //     const dataUpdates = {
  //       clustersCount: clustersResponse.maxItems || 0,
  //       surfacesCount: surfacesResponse.maxItems || 0,
  //       unprocessedSensorsCount: unprocessedSensorsCount,
  //       lastUpdated: new Date().toLocaleTimeString()
  //     };

  //     let status = 'idle';
  //     let isRunning = false;
  //     let logData = { logs: [], podLogs: [], combinedLogs: [], lastLog: null };
  //     let podName = null;

  //     try {
  //       const podStatusResponse = await apiClient.post('/pod-status', {
  //         projectName: projectName,
  //         projectId: parseInt(projectId),
  //         subType: "raytracing"
  //       }, {
  //         useOperational: 'execution'
  //       });

  //       if (podStatusResponse.status === 'Running') {
  //         status = 'running';
  //         isRunning = true;
  //       } else if (podStatusResponse.status === 'Completed') {
  //         if (unprocessedSensorsCount === 0) {
  //           status = 'completed';
  //         } else {
  //           status = 'running'; 
  //           isRunning = true;
  //         }
  //       } else if (podStatusResponse.status === 'Failed') {
  //         status = 'error';
  //       }

  //       logData = processLogs(podStatusResponse.logs, podStatusResponse.podLog);
  //       podName = podStatusResponse.podName;

  //     } catch (podError) {
  //       console.warn('Pod status check failed:', podError);
  //       status = 'idle';
        
  //       const errorMessage = podError.message || 'No pod found for project';
  //       let logMessage = `Error checking pod status: ${errorMessage}`;
  //       let logType = 'error';
        
  //       // Handle "No pod found for project" case differently
  //       if (errorMessage.includes('No pod found for project')) {
  //         logType = 'info';
  //         const unprocessedCount = dataUpdates.unprocessedSensorsCount;
          
  //         if (unprocessedCount === 0) {
  //           logMessage = `Server log disappeared. Execution is finished - no unprocessed sensors remaining.`;
  //           status = 'completed';
  //         } else {
  //           logMessage = `Server log disappeared. Something may have happened - ${unprocessedCount} sensors still unprocessed.`;
  //           status = 'error';
  //         }
  //       }
        
  //       logData = {
  //         logs: [logMessage],
  //         podLogs: [],
  //         combinedLogs: [{ text: logMessage, type: logType }],
  //         lastLog: { text: logMessage, type: logType }
  //       };
  //     }

  //     setRayTracingStatus(prev => ({
  //       ...prev,
  //       status,
  //       isRunning,
  //       ...logData,
  //       podName,
  //       ...dataUpdates
  //     }));
      
  //   } catch (error) {
  //     console.error('Error checking raytracing status:', error);
      
  //     const errorMessage = error.message || 'Unknown error';
  //     let logMessage = `Error checking status: ${errorMessage}`;
  //     let logType = 'error';
  //     let finalStatus = 'error';
      
  //     // Handle "No pod found for project" case differently
  //     if (errorMessage.includes('No pod found for project')) {
  //       logType = 'info';
  //       logMessage = `Server log disappeared. Check unprocessed sensors to determine if execution finished.`;
  //       finalStatus = 'idle';
  //     }
      
  //     setRayTracingStatus(prev => ({
  //       ...prev,
  //       status: finalStatus,
  //       logs: [logMessage],
  //       combinedLogs: [{ text: logMessage, type: logType }],
  //       lastLog: { text: logMessage, type: logType },
  //       lastUpdated: new Date().toLocaleTimeString()
  //     }));
  //   } finally {
  //     setLoading('rayTracingStatus', false);
  //   }
  // };

  // Confirmation Dialog Component
  const ConfirmationDialog = () => {
    if (!confirmationDialog.isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">{confirmationDialog.title}</h3>
          </div>
          
          <p className="text-slate-600 mb-2">{confirmationDialog.message}</p>
          
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 text-sm">Please Note</h4>
                <p className="text-xs text-amber-700">
                  This operation may take several minutes to complete. You'll see progress indicators in the interface.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: null, entityType: '' })}
              className="flex-1 px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmationDialog.onConfirm}
              className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors duration-200"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Status indicator component
  const StatusIndicator = ({ status, isRunning }) => {
    const getStatusConfig = () => {
      if (isRunning || status === 'running') {
        return {
          icon: Loader,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          text: 'Running',
          animate: true
        };
      }
      
      switch (status) {
        case 'completed':
          return {
            icon: CheckCircle2,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
            text: 'Completed'
          };
        case 'error':
          return {
            icon: XCircle,
            color: 'text-red-600',
            bgColor: 'bg-red-100',
            text: 'Error'
          };
        case 'idle':
        default:
          return {
            icon: Clock,
            color: 'text-slate-600',
            bgColor: 'bg-slate-100',
            text: 'Idle'
          };
      }
    };

    const config = getStatusConfig();
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${config.bgColor}`}>
        <Icon className={`w-4 h-4 ${config.color} ${config.animate ? 'animate-spin' : ''}`} />
        <span className={`text-sm font-semibold ${config.color}`}>
          {config.text}
        </span>
      </div>
    );
  };

  // Data counts component
  // const DataCounts = ({ status }) => {
  //   const counts = [
  //     {
  //       label: 'Unprocessed Sensors',
  //       value: status.unprocessedSensorsCount,
  //       icon: Radio,
  //       color: 'text-purple-600'
  //     }
  //   ];

  //   return (
  //     <div className="grid grid-cols-1 gap-3 mb-4">
  //       {counts.map((count, index) => {
  //         const Icon = count.icon;
  //         return (
  //           <div key={index} className="bg-white rounded-lg p-3 border border-slate-200">
  //             <div className="flex items-center gap-2 mb-1">
  //               <Icon className={`w-4 h-4 ${count.color}`} />
  //               <span className="text-xs font-medium text-slate-600">{count.label}</span>
  //             </div>
  //             <div className="text-lg font-bold text-slate-900">
  //               {count.value !== null ? count.value : '-'}
  //             </div>
  //           </div>
  //         );
  //       })}
  //     </div>
  //   );
  // };
// Data counts component
const DataCounts = ({ status }) => {
  const counts = [
    {
      label: 'Total Sensors',
      value: status.sensorsCount,
      icon: Radio,
      color: 'text-blue-600'
    },
    {
      label: 'Unprocessed Sensors',
      value: status.unprocessedSensorsCount,
      icon: AlertCircle,
      color: 'text-amber-600'
    }
  ];

  return (
    <div className="grid grid-cols-2 gap-3 mb-4">
      {counts.map((count, index) => {
        const Icon = count.icon;
        return (
          <div key={index} className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Icon className={`w-4 h-4 ${count.color}`} />
              <span className="text-xs font-medium text-slate-600">{count.label}</span>
            </div>
            <div className="text-lg font-bold text-slate-900">
              {count.value !== null ? count.value : '-'}
            </div>
          </div>
        );
      })}
    </div>
  );
};
  // Action button component
  const ActionButton = ({ 
    onClick, 
    loading, 
    disabled, 
    icon: Icon, 
    children, 
    variant = 'primary' 
  }) => {
    const getVariantClasses = () => {
      switch (variant) {
        case 'danger':
          return 'bg-red-600 hover:bg-red-700 text-white';
        case 'secondary':
          return 'bg-slate-600 hover:bg-slate-700 text-white';
        case 'outline':
          return 'bg-white hover:bg-slate-50 text-slate-700 border-2 border-slate-200 hover:border-slate-300';
        default:
          return 'bg-blue-600 hover:bg-blue-700 text-white';
      }
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled || loading}
        className={`group flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${getVariantClasses()}`}
      >
        {loading ? (
          <Loader className="w-5 h-5 animate-spin" />
        ) : (
          <Icon className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
        )}
        {children}
      </button>
    );
  };

  // Clear Results Section Component
  const ClearResultsSection = () => {
    const deleteItems = [
      {
        label: 'Sensors',
        description: 'Delete all sensor data',
        icon: Radio,
        color: 'text-slate-600',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
        onClick: deleteSensors,
        loading: loadingStates.deleteSensors,
         count: preRayTracingStatus.sensorsCount 
        // count: preRayTracingStatus.unprocessedSensorsCount
      },
      {
        label: 'Surfaces',
        description: 'Delete all surface data',
        icon: Layers,
        color: 'text-slate-600',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
        onClick: deleteSurfaces,
        loading: loadingStates.deleteSurfaces,
        count: preRayTracingStatus.surfacesCount
      },
      {
        label: 'Clusters',
        description: 'Delete all cluster data',
        icon: Database,
        color: 'text-slate-600',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
        onClick: deleteClusters,
        loading: loadingStates.deleteClusters,
        count: preRayTracingStatus.clustersCount
      }
    ];

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-600 to-slate-700 p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl">
                <Trash2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Clear Results</h2>
                <p className="text-white/80 text-sm">Remove existing data</p>
              </div>
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/10 rounded-full"></div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Progress indicator when any deletion is in progress */}
          {(loadingStates.deleteSensors || loadingStates.deleteSurfaces || loadingStates.deleteClusters) && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                <div>
                  <h4 className="font-medium text-blue-800 text-sm">Deletion in Progress</h4>
                  <p className="text-xs text-blue-700">
                    This operation may take several minutes. Please wait...
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {deleteItems.map((item, index) => {
              const Icon = item.icon;
              const isDeleting = item.loading;
              return (
                <div
                  key={index}
                  className={`${item.bgColor} ${item.borderColor} border rounded-lg p-3 transition-all duration-200 hover:shadow-sm ${
                    isDeleting ? 'ring-2 ring-blue-200 bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg shadow-sm ${
                        isDeleting ? 'bg-blue-100' : 'bg-white'
                      }`}>
                        {isDeleting ? (
                          <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                        ) : (
                          <Icon className={`w-4 h-4 ${item.color}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2">
                          {item.label}
                          {item.count !== null && (
                            <span className={`px-1.5 py-0.5 text-xs rounded ${
                              isDeleting ? 'bg-blue-200 text-blue-800' : 'bg-slate-200 text-slate-700'
                            }`}>
                              {item.count}
                            </span>
                          )}
                          {isDeleting && (
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-200 text-blue-800 animate-pulse">
                              Deleting...
                            </span>
                          )}
                        </h3>
                        <p className="text-xs text-slate-600 truncate">
                          {isDeleting ? 'Deletion in progress, please wait...' : item.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={item.onClick}
                      disabled={item.loading || (loadingStates.deleteSensors || loadingStates.deleteSurfaces || loadingStates.deleteClusters)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isDeleting 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {item.loading ? (
                        <>
                          <Loader className="w-3 h-3 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 text-sm">Warning</h4>
                <p className="text-xs text-amber-700">
                  Deleting data is irreversible. Ensure backups exist before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Section component
  const ProcessingSection = ({ 
    title, 
    subtitle, 
    icon: Icon, 
    gradientColors, 
    status, 
    onRun, 
    onStop, 
    onCheckStatus, 
    loadingStates: sectionLoadingStates 
  }) => (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className={`${gradientColors} p-6 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{title}</h2>
              <p className="text-white/80 text-sm">{subtitle}</p>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/10 rounded-full"></div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Status Display */}
        <div className="mb-6 p-4 bg-slate-50 rounded-xl">
          <div className="mb-3">
            <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              Current Status
            </span>
          </div>
          
          {/* Data Counts */}
          <DataCounts status={status} />
          
          {status.podName && (
            <p className="text-xs text-slate-500 mb-1">
              Pod: {status.podName}
            </p>
          )}
          {status.lastUpdated && (
            <p className="text-xs text-slate-500">
              Last updated: {status.lastUpdated}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <ActionButton
            onClick={onRun}
            loading={sectionLoadingStates.start}
            icon={Play}
          >
            {sectionLoadingStates.start ? 'Starting...' : `Run ${title}`}
          </ActionButton>

          <ActionButton
            onClick={onCheckStatus}
            loading={sectionLoadingStates.status}
            icon={RefreshCw}
            variant="outline"
          >
            {sectionLoadingStates.status ? 'Checking...' : 'Check Status'}
          </ActionButton>

          <ActionButton
            onClick={onStop}
            loading={sectionLoadingStates.stop}
            icon={Square}
            variant="danger"
          >
            {sectionLoadingStates.stop ? 'Stopping...' : `Stop ${title}`}
          </ActionButton>
        </div>

        {/* Last Log */}
        {status.lastLog && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider flex items-center gap-2">
              <Terminal className="w-4 h-4" />
              Last Log
            </h4>
            <div className={`rounded-lg p-3 text-sm font-mono ${
              status.lastLog.type === 'error' 
                ? 'bg-red-50 text-red-700 border border-red-200' 
                : status.lastLog.type === 'pod'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : status.lastLog.type === 'info'
                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              <span className={`text-xs px-2 py-1 rounded ${
                status.lastLog.type === 'error' 
                  ? 'bg-red-200 text-red-800' 
                  : status.lastLog.type === 'pod'
                  ? 'bg-blue-200 text-blue-800'
                  : status.lastLog.type === 'info'
                  ? 'bg-amber-200 text-amber-800'
                  : 'bg-green-200 text-green-800'
              }`}>
                {status.lastLog.type === 'pod' ? 'POD' : status.lastLog.type === 'error' ? 'ERROR' : status.lastLog.type === 'info' ? 'INFO' : 'EXEC'}
              </span>
              <div className="mt-2">
                {status.lastLog.text}
              </div>
            </div>
          </div>
        )}

        {/* System Logs */}
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider flex items-center gap-2">
            <Eye className="w-4 h-4" />
            System Logs
          </h4>
          <div className="bg-slate-900 rounded-lg p-4 text-sm max-h-64 overflow-y-auto">
            {status.combinedLogs.length > 0 ? (
              status.combinedLogs.map((log, index) => (
                <div key={index} className="mb-2 flex items-start gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded font-mono flex-shrink-0 ${
                    log.type === 'error' 
                      ? 'bg-red-600 text-red-100' 
                      : log.type === 'pod'
                      ? 'bg-blue-600 text-blue-100'
                      : log.type === 'info'
                      ? 'bg-amber-600 text-amber-100'
                      : 'bg-green-600 text-green-100'
                  }`}>
                    {log.type === 'pod' ? 'POD' : log.type === 'error' ? 'ERR' : log.type === 'info' ? 'INFO' : 'EXEC'}
                  </span>
                  <span className={`font-mono ${
                    log.type === 'error' 
                      ? 'text-red-400' 
                      : log.type === 'pod'
                      ? 'text-blue-400'
                      : log.type === 'info'
                      ? 'text-amber-400'
                      : 'text-green-400'
                  }`}>
                    {log.text}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-slate-500 font-mono">No system logs available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoadingProject) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading project information...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
              <Zap className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
              Ray Tracing Execution
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Execute and monitor ray tracing processes for{' '}
              <span className="font-bold text-blue-600">{projectName || projectId}</span>
            </p>
          </div>

          {/* Back Button */}
          <div className="mb-8">
            <button 
              onClick={() => navigate(`/project-processing/${projectId}`)}
              className="group bg-white hover:bg-slate-50 text-slate-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg border border-slate-200"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
              Back to Project Menu
            </button>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Clear Results Section */}
            <ClearResultsSection />
            
            {/* Pre-Ray Tracing Section */}
            <ProcessingSection
              title="Pre-Ray Tracing"
              subtitle="Prepare scene and setup calculations"
              icon={Settings}
              gradientColors="bg-gradient-to-br from-blue-600 to-indigo-600"
              status={preRayTracingStatus}
              onRun={runPreRayTracing}
              onStop={stopPreRayTracing}
              onCheckStatus={checkPreRayTracingStatus}
              loadingStates={{
                start: loadingStates.preRayTracingStart,
                stop: loadingStates.preRayTracingStop,
                status: loadingStates.preRayTracingStatus
              }}
            />

            {/* Ray Tracing Section */}
            <ProcessingSection
              title="Ray Tracing"
              subtitle="Execute main ray tracing calculations"
              icon={Zap}
              gradientColors="bg-gradient-to-br from-purple-600 to-pink-600"
              status={rayTracingStatus}
              onRun={runRayTracing}
              onStop={stopRayTracing}
              onCheckStatus={checkRayTracingStatus}
              loadingStates={{
                start: loadingStates.rayTracingStart,
                stop: loadingStates.rayTracingStop,
                status: loadingStates.rayTracingStatus
              }}
            />

          </div>

          {/* Debug Info */}
          <div className="mt-8 bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Debug Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
              <div>
                <strong>Project ID:</strong> {projectId}
              </div>
              <div>
                <strong>Project Name:</strong> {projectName || 'Loading...'}
              </div>
              <div>
                <strong>Pod Prefix:</strong> {projectName || 'N/A'}
              </div>
              <div>
                <strong>Pre-Ray Pod:</strong> {preRayTracingStatus.podName || 'N/A'}
              </div>
              <div>
                <strong>Ray Tracing Pod:</strong> {rayTracingStatus.podName || 'N/A'}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </MainLayout>
  );
};

export default RayTracingExecution;
